import React, { FC, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useNavigate } from 'react-router-dom';
import { Card, Divider, Form } from 'antd';
import { LangSwitcher } from 'features/LangSwitcher';
import { Button } from 'shared/ui/Button';
import { ModernInput as Input } from 'shared/ui/ModernInput';
import { ModernInputPassword } from 'shared/ui/ModernInputPassword';
import { Checkbox } from 'shared/ui/Checkbox';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useLoginMutation } from 'entities/User';
// import { GoogleAuthButton } from 'features/GoogleAuthButton';
import { YandexAuthButton } from 'features/YandexAuthButton';
import { AppLogo } from 'app/appState';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { isErrorApiResponse } from 'shared/utils/helpers/isErrorApiResponse';
import { authFormActions } from '../model/slice/authFormSlice';
import { LoginFormValues } from '../model/types';
import { VkAuthButton } from 'features/VkAuthButton';

interface AuthFormProps {
  className?: string;
}

export const AuthForm: FC<AuthFormProps> = (props) => {
  const { className } = props;
  const navigate = useNavigate();
  const { t } = useAppTranslation(['login-page', 'common', 'booking']);

  const dispatch = useAppDispatch();

  const [form] = Form.useForm();

  const [login, { isLoading }] = useLoginMutation();

  const handleSuccessGoogleAuth = useCallback((): void => {
    navigate(AppRoutes.HOME);
  }, [navigate]);

  const handleFailGoogleAuth = useCallback(
    (error: string): void => {
      if (isErrorApiResponse(error)) {
        showNotification('error', t('Error', { ns: 'common' }), t(error.data.message));
      } else {
        console.log(error);
      }
    },
    [t],
  );

  const handleSubmit = useCallback(
    async (values: LoginFormValues): Promise<void> => {
      const { email, password, rememberUser } = values;

      try {
        await login({ loginData: { email, password }, remember: rememberUser }).unwrap();
        navigate(AppRoutes.HOME);
      } catch (error: CustomAny) {
        if (isErrorApiResponse(error)) {
          const errorMessage = Array.isArray(error.data.message)
            ? error.data.message.map((message) => t(message, { ns: 'common' }))
            : t(error.data.message, { ns: 'common' });
          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        } else {
          console.log(error);
        }
      }
    },
    [login, navigate, t],
  );

  const goToBookingPage = useCallback((): void => {
    navigate(AppRoutes.BOOKING);
  }, [navigate]);

  const openForgotPasswordScreen = useCallback((): void => {
    dispatch(authFormActions.changeMode('resetPassword'));
  }, [dispatch]);

  return (
    <Card
      className={className}
      title={
        <div className="flex flex-col justify-center p-1 items-center">
          <div className="relative w-full flex items-center justify-center">
            <AppLogo width={180} height="100%" className="w-[180px]" />
            <div className="absolute right-0">
              <LangSwitcher mobileBackgroundClassName="bg-white" isDesktop />
            </div>
          </div>
          <div className="text-3xl font-semibold mt-4 font-tektur">{t('Log in', { ns: 'booking' })}</div>
        </div>
      }
    >
      <Form name="basic" layout="vertical" initialValues={{ rememberUser: true }} form={form} onFinish={handleSubmit}>
        <div className="flex gap-4 items-center justify-center">
          <YandexAuthButton onSuccessAuth={handleSuccessGoogleAuth} onFailAuth={handleFailGoogleAuth} />
          <VkAuthButton className="flex gap-4 items-center" onSuccessAuth={handleSuccessGoogleAuth} onFailAuth={handleFailGoogleAuth} />
        </div>
        <Divider className="!border-grey !text-grey">
          <span className="text-grey">{t('OR')}</span>
        </Divider>
        <Form.Item
          name="email"
          required={false}
          label={<span className="text-2xl font-tektur">{t('Email', { ns: 'booking' })}</span>}
          rules={[
            { required: true, message: t('Please enter your email!') },
            { type: 'email', message: t('Please enter correct email!') },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="password"
          required={false}
          label={<span className="text-2xl font-tektur">{t('Password', { ns: 'booking' })}</span>}
          rules={[
            { required: true, message: t('Please enter your password!') },
            { min: 8, message: t('Password must be at least 8 characters!') },
          ]}
        >
          <ModernInputPassword />
        </Form.Item>
        <div className="flex justify-between items-center mb-6">
          <Form.Item name="rememberUser" valuePropName="checked" className="mb-0">
            <Checkbox name="rememberUser" defaultChecked>
              <span className="text-grey">{t('Remember me')}</span>
            </Checkbox>
          </Form.Item>
          <div className="text-black cursor-pointer underline" onClick={openForgotPasswordScreen}>
            {t('Forgot password?')}
          </div>
        </div>
        <Form.Item className="!mb-4">
          <Button type="submit" containerClassName="w-full" isLoading={isLoading}>
            {t('Login')}
          </Button>
        </Form.Item>
        <Form.Item className="!mb-2">
          <Button containerClassName="w-full" onClick={goToBookingPage} theme="outlined" className="!text-accent !border-accent">
            {t('Go to booking')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
